import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import {
  type AddressPassRecord,
  type AddressedRecord,
  readAddressPass,
  readAddressedRecord,
} from '@/lib/content-doctrine/article-address';
import {
  ArticleCritiqueDraftSchema,
  type ArticleCritiqueReport,
} from '@/lib/content-doctrine/article-critique';
import {
  type ClaimCheckConfirmation,
  articleCritiqueFindingKey,
  confirmCritiqueFindings,
  decidedFindingKeys,
} from '@/lib/content-doctrine/article-critique-confirmation';
import { reviewerLimitFindingKeys } from '@/lib/content-doctrine/article-critique-limits';
import { readWriterLedger } from '@/lib/content-doctrine/article-evidence';
import {
  type ArticleFactCheckSummary,
  type StoredFactCheck,
  readFactCheck,
  summariseFactCheck,
} from '@/lib/content-doctrine/article-factcheck';
import { type ImportProvenance, readImportProvenance } from '@/lib/content-doctrine/article-import';
import {
  type ArticleReleaseInput,
  type ArticleReviewState,
  articleReviewState,
} from '@/lib/content-doctrine/article-release';
import { WEB_READINESS_CHECK_IDS, isCurrentRubric } from '@/lib/content-doctrine/web-readiness';
import { hashContent } from '@/lib/knowledge/extract-document';
import { articleChangedAt } from '@/lib/reach/editorial-article-changed-at';

/**
 * What an article went through, read back out of the turn that produced it.
 *
 * 🔴 WHY THIS IS WORTH BUILDING. The founder, comparing us to SpreadJam
 * (2026-09-06): _"how do I know what checks the article went through? On
 * SpreadJam, it shows all the steps that it went through for the article, and it
 * looks nice."_ Their surface is a 9-item checklist headed "Workflow Progress
 * 4 / 9 completed (44%)" — captured in
 * `docs/research/competitors/spreadjam/artifacts/OBSERVED-2026-09-06-gate-and-workflow.md`.
 *
 * We hold strictly MORE than they show and were surfacing none of it. Every
 * article Mora writes carries an evidence-class ladder per source, a retrieval
 * provenance saying whether a URL was actually fetched or merely cited by a
 * search model, a 14-check readiness verdict with `{detail, fix}` findings, and
 * a `provenance` block saying whether the voice and the topic identity were
 * resolved from the workspace or supplied by the model. SpreadJam shows a tick
 * per step; this can show WHY each tick is earned.
 *
 * ── WHERE IT LIVES, WHICH IS NOT OBVIOUS ────────────────────────────────────
 * Nowhere durable. There is no brief table: `build_article_brief` records its
 * verdict in a per-REQUEST `AsyncLocalStorage` bag that dies with the turn. The
 * only lasting copy is the tool output persisted into `ai_messages.parts` — the
 * same row `project_tasks.artifact_id` already points at, because
 * `validate_project_artifact_ref` requires a document artifact to be an
 * `ai_messages.id`. So one read of that message yields both the article and its
 * whole provenance, and no new table is needed.
 *
 * A turn may call `build_article_brief` several times (fix-and-retry is the
 * design — a first attempt usually fails). Every attempt is kept: the count is
 * itself the honest signal, and the LAST one is the brief the article was
 * actually written to.
 */

export type ArticleStepState = 'done' | 'pending' | 'unknown' | 'failed';

export interface ArticleStep {
  id: string;
  label: string;
  state: ArticleStepState;
  /** One short line of evidence. Never a restatement of the label. */
  detail?: string;
}

export interface ArticleSourceSummary {
  title: string;
  url: string;
  evidenceClass: string;
  retrieval: string;
  hasReceipt: boolean;
}

/**
 * A `content-doctrine/web-readiness.ts` finding, read back verbatim from
 * `tool-generate_document`'s own `webReadiness.findings` — never
 * recomputed here. `generate_document`'s `execute()` already has the real
 * `citableSources` (from the fire-lock's `ArticleBriefGateEntry`) at the
 * moment it runs `checkWebReadiness`, so the stored verdict is already the
 * complete one; re-running the check client-side from the persisted brief
 * would only risk drifting from it.
 */
export interface WebReadinessFindingSummary {
  id: string;
  severity: 'fail' | 'warn';
  detail: string;
  fix: string;
}

export interface EditorialWebMetadata {
  metaTitle: string | null;
  metaDescription: string | null;
  slug: string | null;
}

export interface EditorialWebReadiness {
  ready: boolean;
  score: number;
  staleReason: string | null;
  checks: Array<{
    id: string;
    state: 'passed' | 'fail' | 'warn' | 'not_applied' | 'unknown';
    findings: WebReadinessFindingSummary[];
  }>;
  findings: WebReadinessFindingSummary[];
  /**
   * Whether this stored verdict was computed against the checks currently in
   * force. False for anything drafted before `checkedBy` existed, and for
   * anything drafted before a later check was added.
   *
   * The surface must NOT render a stale score as if it ranked against a fresh
   * one — see `WebReadinessReport.checkedBy` for the day that went wrong.
   */
  isCurrent: boolean;
  /**
   * How many of the rubric's checks actually RAN on this draft, and how many
   * exist. Three checks are conditional (see `WebReadinessReport.checksApplied`)
   * so a bare 100 can mean "passed eight of eleven". The surface states the
   * denominator rather than letting the number imply a full sweep.
   */
  applied: number;
  total: number;
}

export interface EditorialArticleScore extends ArticleReleaseInput {
  webReadiness: EditorialWebReadiness;
  contentHash: string | null;
  critiqueCurrent: boolean;
  critiqueStatus: string | null;
  /** Current human work, distinct from the mechanically-scored web checks. */
  reviewState: ArticleReviewState;
  unresolvedEditorialFindings: number;
  languageFindings: number;
  /** The stored claim check, resolved against this article's hash. Null when none has run. */
  factCheck: ArticleFactCheckSummary | null;
}

export interface EditorialArticleTrail {
  editorialCritique: {
    report: ArticleCritiqueReport;
    isCurrent: boolean;
    reviewedFindingKeys: ReadonlySet<string>;
    /**
     * Findings a CURRENT claim check has already answered with a verified quote (`confirmCritiqueFindings`).
     * They are not open editorial decisions; the panel shows the quote that answers each one.
     */
    claimCheckConfirmations?: ReadonlyMap<string, ClaimCheckConfirmation>;
    /**
     * Findings whose substance is "the reviewer could not see the source text" (`reviewerLimitFindingKeys`). They are
     * shown with the review's limitations and never count as open findings.
     */
    reviewerLimitKeys?: ReadonlySet<string>;
  } | null;
  /** What the automatic fixer did to produce THIS version, with each edit; null for a version it did not make. */
  addressed?: AddressedRecord | null;
  /** The pass that ran against THIS version and did not (or did, but was superseded): its refusals stay readable. */
  addressPass?: AddressPassRecord | null;
  documentId: string | null;
  editingPass: { findings: string[]; isHistorical: boolean } | null;
  title: string | null;
  markdown: string | null;
  /** SHA-256 of the current Markdown revision, used by approval and publication receipts. */
  contentHash: string | null;
  /** Set when the article was written outside Mora's writer and imported; null for every other article. */
  importProvenance?: ImportProvenance | null;
  wordCount: number | null;
  /** How many times the brief was built before one passed. */
  briefAttempts: number;
  draftable: boolean | null;
  sources: ArticleSourceSummary[];
  /** `verified` / `default` / `unverified`, per field. */
  provenance: Record<string, string> | null;
  targetQuestion: string | null;
  claimsToAvoid: string[];
  /** Title tag / description / slug — present only for a `blog`/`article` draft. */
  webMetadata: EditorialWebMetadata | null;
  /** The mechanical publish-readiness verdict — present only for a `blog`/`article` draft. */
  webReadiness: EditorialWebReadiness | null;
  /**
   * The claim check against fetched primary sources (`factcheck` job), read back
   * from `output.factCheck`. Its own record: it never changes the article, and
   * `isCurrent` says whether it describes the article exactly as it stands now.
   */
  factCheck?: StoredFactCheck | null;
  /**
   * What the drafting job actually read, recorded ON the artifact at write
   * time. Authoritative over `sources`, which is rebuilt from this message's
   * own tool parts — a durable-pipeline draft researches inside its job, not
   * the chat message, so those parts can show "0 retrieved" for an article
   * that read every source. Null for articles written before it was recorded.
   */
  evidence: { sourcesRetrieved: number; sourcesSupplied: number } | null;
  /**
   * The claims ledger an imported article's writer recorded: how many sentences it tied to a page and a quote,
   * and across how many pages. A record of what the writer says it read, not of what Mora fetched; the
   * judges verify each quote against the fetched page before using it.
   */
  writerLedger?: { claims: number; sources: number } | null;
  steps: ArticleStep[];
}

/**
 * Review decisions were added after some article-trail query results had
 * already been cached in the browser. Treat those legacy values as an empty
 * decision set instead of allowing a stale object to take down the Reach
 * dock while the fresh query is loading. Arrays are accepted defensively for
 * early persisted drafts; the durable representation is a Set.
 */
export function normalizeReviewedFindingKeys(value: unknown): ReadonlySet<string> {
  if (value instanceof Set) return value;
  if (Array.isArray(value)) {
    return new Set(value.filter((entry): entry is string => typeof entry === 'string'));
  }
  if (
    value &&
    typeof value === 'object' &&
    typeof (value as { has?: unknown }).has === 'function'
  ) {
    return value as ReadonlySet<string>;
  }
  return new Set<string>();
}

function normalizeEditorialArticleTrail(
  trail: EditorialArticleTrail | null,
): EditorialArticleTrail | null {
  if (!trail?.editorialCritique) return trail;
  const reviewedFindingKeys = normalizeReviewedFindingKeys(
    trail.editorialCritique.reviewedFindingKeys,
  );
  return {
    ...trail,
    editorialCritique: {
      ...trail.editorialCritique,
      reviewedFindingKeys,
    },
  };
}

interface Part {
  type?: string;
  output?: Record<string, unknown>;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * A quote is anchored to one content hash, so this is a stable enough identity
 * for a human decision while avoiding a second copy of editorial findings. The
 * one definition lives beside the claim-check confirmation that shares it.
 */
export { articleCritiqueFindingKey };

function readReviewedFindingKeys(
  documentOut: Record<string, unknown> | null,
  contentHash: string | undefined,
): ReadonlySet<string> {
  const decisions = asRecord(documentOut?.editorialReviewDecisions);
  if (!decisions || decisions.contentHash !== contentHash) return new Set();
  const values = Array.isArray(decisions.reviewedFindingKeys)
    ? decisions.reviewedFindingKeys.filter((value): value is string => typeof value === 'string')
    : [];
  return new Set(values);
}

/**
 * The stored `webReadiness` verdict, read back verbatim from a
 * `tool-generate_document` output — never recomputed. Exported because the
 * table needs the same verdict for many articles at once and a second reader
 * would be a second opinion about one fact.
 */
export function readWebReadiness(
  documentOut: Record<string, unknown> | null,
  currentContentHash?: string,
): EditorialWebReadiness | null {
  const raw = asRecord(documentOut?.webReadiness);
  if (!raw) return null;
  const stringArray = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every((entry) => typeof entry === 'string');
  const checkedBy = stringArray(raw.checkedBy) ? raw.checkedBy : [];
  const historicalChecks = stringArray(raw.checkedByBeforeEdit) ? raw.checkedByBeforeEdit : [];
  const applied = stringArray(raw.checksApplied) ? raw.checksApplied : [];
  const review = asRecord(documentOut?.articleReview);
  const reviewedCurrent = Boolean(
    currentContentHash &&
    review?.contentHash === currentContentHash &&
    raw.contentHash === currentContentHash,
  );
  const edited =
    raw.invalidatedReason === 'document_edited' ||
    (Boolean(documentOut?.editedAt) && !reviewedCurrent) ||
    (Boolean(review) && !reviewedCurrent);
  const recordedChecks = edited && historicalChecks.length ? historicalChecks : checkedBy;
  const findings: WebReadinessFindingSummary[] = [];
  let validFindings = Array.isArray(raw.findings);
  for (const entry of Array.isArray(raw.findings) ? raw.findings : []) {
    const finding = asRecord(entry);
    if (
      !finding ||
      typeof finding.id !== 'string' ||
      typeof finding.detail !== 'string' ||
      typeof finding.fix !== 'string' ||
      !['fail', 'warn'].includes(String(finding.severity))
    ) {
      validFindings = false;
      continue;
    }
    findings.push({
      id: finding.id,
      detail: finding.detail,
      fix: finding.fix,
      severity: finding.severity as 'fail' | 'warn',
    });
  }
  const valid =
    validFindings &&
    typeof raw.ready === 'boolean' &&
    typeof raw.score === 'number' &&
    Number.isFinite(raw.score) &&
    raw.score >= 0 &&
    raw.score <= 100 &&
    stringArray(raw.checkedBy) &&
    stringArray(raw.checksApplied) &&
    applied.length > 0 &&
    new Set(applied).size === applied.length &&
    new Set(recordedChecks).size === recordedChecks.length &&
    applied.every((id) => recordedChecks.includes(id)) &&
    findings.every((finding) => applied.includes(finding.id)) &&
    raw.ready === !findings.some((finding) => finding.severity === 'fail');
  const staleReason = edited
    ? 'The article changed after these checks. Check this version again.'
    : !valid
      ? 'The saved checks are incomplete or inconsistent. Check this version again.'
      : !isCurrentRubric(checkedBy)
        ? 'These checks used an older rubric. Check this version again.'
        : null;
  return {
    ready: staleReason === null && raw.ready === true,
    score:
      typeof raw.score === 'number' && Number.isFinite(raw.score)
        ? Math.max(0, Math.min(100, raw.score))
        : 0,
    applied: applied.length,
    total: recordedChecks.length,
    isCurrent: staleReason === null,
    staleReason,
    findings,
    checks: [...new Set([...WEB_READINESS_CHECK_IDS, ...recordedChecks])].map((id) => {
      const checkFindings = findings.filter((finding) => finding.id === id);
      return {
        id,
        state:
          !valid || !recordedChecks.includes(id)
            ? ('unknown' as const)
            : !applied.includes(id)
              ? ('not_applied' as const)
              : checkFindings.some((finding) => finding.severity === 'fail')
                ? ('fail' as const)
                : checkFindings.length
                  ? ('warn' as const)
                  : ('passed' as const),
        findings: checkFindings,
      };
    }),
  };
}

function lastDocumentOutput(parts: Part[]): Record<string, unknown> | null {
  const documents = parts.filter((p) => p?.type === 'tool-generate_document' && p.output);
  return documents.length ? (documents[documents.length - 1]?.output ?? null) : null;
}

/**
 * One readiness verdict per written article, in ONE query.
 *
 * The table renders a score ring per row, and the obvious implementation —
 * `useEditorialArticleTrail` per row — is 32 subscriptions and 32 round trips
 * to render 32 small numbers, each pulling a full article's markdown back with
 * it. This asks for the same rows once. It takes only the artifact ids of rows
 * that actually HAVE an article, so a programme of 32 proposals with one
 * written article costs a single-row query, not thirty-two empty ones.
 *
 * It returns a Map rather than an array so a row can look itself up without
 * scanning, and so "no entry" is distinguishable from "entry with score 0" —
 * the distinction `ScoreRing`'s `unknown` state exists to render.
 */
interface EditorialArticleBulk {
  scores: Map<string, EditorialArticleScore>;
  /** Last change per artifact id, for EVERY article read — including one with no score. */
  changedAt: Map<string, string>;
}

function useEditorialArticleBulk<T>(
  artifactIds: readonly string[],
  select: (bulk: EditorialArticleBulk) => T,
) {
  // Sorted so the key is stable under a re-render that reorders the rows —
  // a filter or a sort must not look like a different query.
  const ids = [...new Set(artifactIds)].sort();
  return useQuery<EditorialArticleBulk, Error, T>({
    queryKey: ['editorial-article-scores', ids],
    enabled: ids.length > 0,
    select,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_messages')
        .select('id, created_at, parts')
        .in('id', ids);
      if (error) throw new Error(error.message);
      const out = new Map<string, EditorialArticleScore>();
      const changedAt = new Map<string, string>();
      for (const row of data ?? []) {
        const parts: Part[] = Array.isArray(row.parts) ? (row.parts as Part[]) : [];
        const output = lastDocumentOutput(parts);
        const changed = articleChangedAt(row.created_at, output);
        if (changed) changedAt.set(row.id, changed);
        const markdownHash =
          typeof output?.markdown === 'string' ? await hashContent(output.markdown) : null;
        const currentContentHash = output?.articleReview ? (markdownHash ?? undefined) : undefined;
        const readiness = readWebReadiness(output, currentContentHash);
        if (!readiness) continue;
        const critiqueRaw = asRecord(output?.editorialCritique);
        const critiqueParsed = ArticleCritiqueDraftSchema.safeParse(critiqueRaw);
        const critiqueCurrent = Boolean(
          currentContentHash &&
          critiqueParsed.success &&
          critiqueRaw?.contentHash === currentContentHash,
        );
        const factCheckStored = readFactCheck(output, markdownHash);
        const decidedKeys = decidedFindingKeys(
          readReviewedFindingKeys(output, currentContentHash),
          critiqueCurrent && critiqueParsed.success
            ? confirmCritiqueFindings(critiqueParsed.data.findings, factCheckStored)
            : undefined,
          critiqueCurrent && critiqueParsed.success
            ? reviewerLimitFindingKeys(critiqueParsed.data, articleCritiqueFindingKey)
            : undefined,
        );
        const unresolvedEditorialFindings =
          critiqueCurrent && critiqueParsed.success
            ? critiqueParsed.data.findings.filter(
                (finding) => !decidedKeys.has(articleCritiqueFindingKey(finding)),
              ).length
            : 0;
        const languageFindings = Array.isArray(output?.editingPass)
          ? output.editingPass.filter((finding): finding is string => typeof finding === 'string')
              .length
          : 0;
        const factCheck = summariseFactCheck(factCheckStored);
        out.set(row.id, {
          webReadiness: readiness,
          contentHash: markdownHash,
          critiqueCurrent,
          critiqueStatus: typeof critiqueRaw?.status === 'string' ? critiqueRaw.status : null,
          unresolvedEditorialFindings,
          languageFindings,
          factCheck,
          reviewState: articleReviewState({
            webReadiness: readiness,
            critiqueCurrent,
            critiqueStatus: critiqueRaw?.status as string | undefined,
            unresolvedEditorialFindings,
            languageFindings,
            factCheck,
          }),
        });
      }
      return { scores: out, changedAt };
    },
  });
}

const selectScores = (bulk: EditorialArticleBulk) => bulk.scores;
const selectChangedAt = (bulk: EditorialArticleBulk) => bulk.changedAt;

export function useEditorialArticleScores(artifactIds: readonly string[]) {
  return useEditorialArticleBulk(artifactIds, selectScores);
}

/** The "Last changed" column: same query and cache entry as the scores, so no extra round trip. */
export function useEditorialArticleChangedAt(artifactIds: readonly string[]) {
  return useEditorialArticleBulk(artifactIds, selectChangedAt);
}

export function useEditorialArticleTrail(artifactId: string | null) {
  return useQuery<EditorialArticleTrail | null, Error, EditorialArticleTrail | null>({
    queryKey: ['editorial-article-trail', artifactId],
    enabled: Boolean(artifactId),
    // `select` also runs against React Query's existing cache. That matters
    // here because a user can leave the app open across a deploy that adds
    // review decisions, leaving a perfectly valid but older trail object in
    // memory without `reviewedFindingKeys`.
    select: normalizeEditorialArticleTrail,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_messages')
        .select('parts')
        .eq('id', artifactId as string)
        .maybeSingle();
      if (error) throw new Error(error.message);

      const parts: Part[] = Array.isArray(data?.parts) ? (data.parts as Part[]) : [];

      const briefParts = parts.filter((p) => p?.type === 'tool-build_article_brief' && p.output);
      const lastBrief = briefParts.length
        ? (briefParts[briefParts.length - 1]?.output ?? null)
        : null;
      const documentParts = parts.filter((p) => p?.type === 'tool-generate_document' && p.output);
      const documentOut = documentParts.length
        ? (documentParts[documentParts.length - 1]?.output ?? null)
        : null;

      const brief = asRecord(lastBrief?.brief);
      const markdown = typeof documentOut?.markdown === 'string' ? documentOut.markdown : null;
      const wordCount = markdown ? markdown.trim().split(/\s+/).length : null;

      const rawSources = Array.isArray(brief?.sources) ? brief.sources : [];
      const receipts = Array.isArray(lastBrief?.sourceReceipts) ? lastBrief.sourceReceipts : [];
      const sources: ArticleSourceSummary[] = rawSources.flatMap((raw) => {
        const s = asRecord(raw);
        if (!s || typeof s.url !== 'string') return [];
        return [
          {
            title: typeof s.title === 'string' ? s.title : s.url,
            url: s.url,
            evidenceClass: typeof s.evidenceClass === 'string' ? s.evidenceClass : 'unknown',
            retrieval: typeof s.retrieval === 'string' ? s.retrieval : 'unknown',
            hasReceipt: receipts.some((rawReceipt) => {
              const receipt = asRecord(rawReceipt);
              return (
                receipt?.sourceId === s.id &&
                receipt?.evidenceKind === 'page_text' &&
                typeof receipt?.retainedTextHash === 'string'
              );
            }),
          },
        ];
      });

      const fetched = sources.filter((s) => s.hasReceipt).length;
      const draftable = typeof lastBrief?.draftable === 'boolean' ? lastBrief.draftable : null;
      const searchedParts = parts.filter(
        (p) => p?.type === 'tool-search_web' || p?.type === 'tool-search_sources',
      ).length;

      // Present only on a `blog`/`article` draft — see `generate-document.ts`'s
      // own `webMetadata`/`webReadiness` doc comments for why the OTHER
      // artifact types never carry these fields at all.
      const webMetadataRaw = asRecord(documentOut?.webMetadata);
      const webMetadata: EditorialWebMetadata | null = webMetadataRaw
        ? {
            metaTitle:
              typeof webMetadataRaw.metaTitle === 'string' ? webMetadataRaw.metaTitle : null,
            metaDescription:
              typeof webMetadataRaw.metaDescription === 'string'
                ? webMetadataRaw.metaDescription
                : null,
            slug: typeof webMetadataRaw.slug === 'string' ? webMetadataRaw.slug : null,
          }
        : null;

      const markdownHash = markdown ? await hashContent(markdown) : null;
      const currentContentHash =
        markdown && documentOut?.articleReview ? (markdownHash ?? undefined) : undefined;
      const webReadiness = readWebReadiness(documentOut, currentContentHash);
      const critiqueRaw = asRecord(documentOut?.editorialCritique);
      const critiqueParsed = ArticleCritiqueDraftSchema.safeParse(critiqueRaw);
      const editorialCritique =
        critiqueRaw &&
        critiqueParsed.success &&
        Array.isArray(critiqueRaw.findings) &&
        Array.isArray(critiqueRaw.limitations) &&
        ['reviewed', 'incomplete', 'unavailable'].includes(String(critiqueRaw.status))
          ? {
              report: critiqueRaw as unknown as ArticleCritiqueReport,
              isCurrent: Boolean(
                currentContentHash && critiqueRaw.contentHash === currentContentHash,
              ),
              reviewedFindingKeys: readReviewedFindingKeys(documentOut, currentContentHash),
              claimCheckConfirmations:
                currentContentHash && critiqueRaw.contentHash === currentContentHash
                  ? confirmCritiqueFindings(
                      (critiqueRaw as unknown as ArticleCritiqueReport).findings,
                      readFactCheck(documentOut, markdownHash),
                    )
                  : new Map<string, ClaimCheckConfirmation>(),
              reviewerLimitKeys: reviewerLimitFindingKeys(
                critiqueRaw as unknown as ArticleCritiqueReport,
                articleCritiqueFindingKey,
              ),
            }
          : null;

      const steps: ArticleStep[] = [
        {
          id: 'research',
          label: fetched > 0 ? 'Sources retrieved' : 'Sources recorded',
          // Naming a source is not reading it. A green tick beside "0 with
          // retrieval receipts" told the reader this step succeeded when the
          // one thing it exists to do — fetch the evidence — never happened.
          state:
            sources.length > 0 && fetched === 0
              ? 'failed'
              : searchedParts > 0 || sources.length > 0
                ? 'done'
                : 'unknown',
          detail: sources.length
            ? // The distinction the whole doctrine rests on: a search model
              // naming a URL is not the same as Mora having opened it.
              `${sources.length} source${sources.length === 1 ? '' : 's'} · ${fetched} with retrieval receipts`
            : undefined,
        },
        {
          id: 'brief',
          label: 'Brief checks',
          state: draftable === true ? 'done' : draftable === false ? 'pending' : 'unknown',
          detail: briefParts.length
            ? `${briefParts.length} attempt${briefParts.length === 1 ? '' : 's'}${
                briefParts.some((part) => part.output?.draftable === false)
                  ? ` — ${briefParts.filter((part) => part.output?.draftable === false).length} needed changes`
                  : ''
              }`
            : undefined,
        },
        {
          id: 'written',
          label: 'Article written',
          state: markdown ? 'done' : 'pending',
          detail: wordCount ? `${wordCount.toLocaleString()} words` : undefined,
        },
        // Only meaningful for a `blog`/`article` draft — `webReadiness` is
        // `null` for every other type, so this step is simply omitted rather
        // than shown as a permanently 'unknown' row that would never resolve.
        ...(webReadiness
          ? [
              {
                id: 'web',
                label: 'Draft checks',
                state: (!webReadiness.isCurrent
                  ? 'unknown'
                  : webReadiness.ready
                    ? 'done'
                    : 'pending') as ArticleStepState,
                // NO DETAIL, EITHER WAY. The readiness block renders its
                // verdict, its score and its findings a few hundred pixels
                // below this step, so any sentence here is the same fact
                // twice in one panel. The first pass removed only the READY
                // sentence and left "2 issues to fix before this can go live"
                // sitting directly above a block reading "2 things to fix
                // first" — half a fix, which is the shape that survives
                // review because it looks like it was already considered.
                //
                // The split that decides it: this step is a PROCESS milestone
                // (did the piece reach this stage), carried entirely by its
                // icon and tone; the block below is the MEASUREMENT (how it
                // did, and what to do). Neither needs the other's words.
                detail: undefined,
              },
            ]
          : []),
      ];

      return {
        editorialCritique,
        documentId: typeof documentOut?.id === 'string' ? documentOut.id : null,
        editingPass: Array.isArray(documentOut?.editingPass)
          ? {
              findings: documentOut.editingPass.filter(
                (finding): finding is string => typeof finding === 'string',
              ),
              isHistorical: Boolean(documentOut?.editedAt),
            }
          : null,
        title: typeof documentOut?.title === 'string' ? documentOut.title : null,
        markdown,
        contentHash: currentContentHash ?? null,
        importProvenance: readImportProvenance(documentOut),
        addressed: readAddressedRecord(documentOut),
        addressPass: readAddressPass(documentOut),
        wordCount,
        briefAttempts: briefParts.length,
        draftable,
        sources,
        provenance: asRecord(lastBrief?.provenance) as Record<string, string> | null,
        targetQuestion: typeof brief?.targetQuestion === 'string' ? brief.targetQuestion : null,
        claimsToAvoid: Array.isArray(brief?.claimsToAvoid)
          ? brief.claimsToAvoid.filter((c): c is string => typeof c === 'string')
          : [],
        webMetadata,
        webReadiness,
        factCheck: readFactCheck(documentOut, markdownHash),
        evidence: (() => {
          const recorded = asRecord(documentOut?.evidence);
          const retrieved = recorded?.sourcesRetrieved;
          const supplied = recorded?.sourcesSupplied;
          return typeof retrieved === 'number' &&
            typeof supplied === 'number' &&
            Number.isInteger(retrieved) &&
            Number.isInteger(supplied) &&
            retrieved >= 0 &&
            retrieved <= supplied
            ? { sourcesRetrieved: retrieved, sourcesSupplied: supplied }
            : null;
        })(),
        writerLedger: (() => {
          const entries = readWriterLedger(documentOut);
          return entries.length
            ? {
                claims: entries.length,
                sources: new Set(entries.map((entry) => entry.sourceUrl)).size,
              }
            : null;
        })(),
        steps,
      };
    },
  });
}
